// ═══════════════════════════════════════════════════════════════════
//  modules/wiki/WikiEditeur.js
//  WikiEditeur
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { wikiEcrireBrouillon, wikiEffacerBrouillon, wikiLireBrouillon } from "./brouillons";
import { WIKI_CATS } from "./constantes";
import { wBtnSm, wGhostSm, wInp, wLbl, wPanel } from "./styles";
import { wikiIlYA } from "./utils";
import { WikiContenu } from "./WikiContenu";

// ═══════════════ Éditeur ═══════════════
export function WikiEditeur({ api, page, categorieParDefaut, preremplissage, onAnnuler, onEnregistre, droits, user }) {
  const isMobile = useIsMobile();
  const nouvelle = !page;
  const pre = preremplissage || {};
  const [titre, setTitre] = useState(page ? page.titre : (pre.titre || ""));
  const [contenu, setContenu] = useState(page ? page.contenu : (pre.contenu || ""));
  const [categorie, setCategorie] = useState(page ? page.categorie : (pre.categorie || categorieParDefaut || "general"));
  const [resume, setResume] = useState(page ? (page.resume || "") : "");
  const [tags, setTags] = useState(page ? (page.tags || "") : (pre.tags || ""));
  const [visibilite, setVisibilite] = useState(page ? page.visibilite : "staff");
  const [commentaire, setCommentaire] = useState("");
  const [iaAssiste, setIaAssiste] = useState(page ? !!page.ia_assiste : false);
  const [apercu, setApercu] = useState("");
  const [onglet, setOnglet] = useState("ecrire");
  const [erreur, setErreur] = useState("");
  const [enCours, setEnCours] = useState("");
  const [relecture, setRelecture] = useState(null);
  const [assistant, setAssistant] = useState(false);
  const [notes, setNotes] = useState("");
  const [brouillonLocal, setBrouillonLocal] = useState(() => wikiLireBrouillon(page));
  const [sauvegardeLe, setSauvegardeLe] = useState(null);
  const zone = useRef(null);
  const fichierRef = useRef(null);
  const premierRendu = useRef(true);

  // Aperçu : 400 ms après la dernière frappe
  useEffect(() => {
    const id = setTimeout(() => {
      api.apercu(contenu).then((r) => setApercu(r.html)).catch(() => {});
    }, 400);
    return () => clearTimeout(id);
  }, [contenu, api]);

  // Brouillon local : 1,5 s après la dernière frappe
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return; }
    const id = setTimeout(() => {
      wikiEcrireBrouillon(page, { titre, contenu, categorie, resume, tags, visibilite, iaAssiste });
      setSauvegardeLe(Date.now());
    }, 1500);
    return () => clearTimeout(id);
  }, [titre, contenu, categorie, resume, tags, visibilite, iaAssiste, page, api]);

  const reprendreBrouillon = () => {
    const b = brouillonLocal;
    setTitre(b.titre || ""); setContenu(b.contenu || "");
    if (b.categorie) setCategorie(b.categorie);
    setResume(b.resume || ""); setTags(b.tags || "");
    if (b.visibilite) setVisibilite(b.visibilite);
    setIaAssiste(!!b.iaAssiste);
    setBrouillonLocal(null);
  };

  const inserer = (texte) => {
    const el = zone.current;
    if (!el) { setContenu((c) => c + texte); return; }
    const [a, b] = [el.selectionStart, el.selectionEnd];
    setContenu((c) => c.slice(0, a) + texte + c.slice(b));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = a + texte.length; }, 0);
  };
  const entourer = (avant, apres) => {
    const el = zone.current;
    if (!el) return;
    const [a, b] = [el.selectionStart, el.selectionEnd];
    const sel = contenu.slice(a, b) || "texte";
    setContenu((c) => c.slice(0, a) + avant + sel + apres + c.slice(b));
    setTimeout(() => { el.focus(); el.selectionStart = a + avant.length; el.selectionEnd = a + avant.length + sel.length; }, 0);
  };

  const enregistrer = async (publier) => {
    if (!titre.trim()) { setErreur("Il faut un titre."); return; }
    setEnCours("save"); setErreur("");
    try {
      const corps = { titre, contenu, categorie, resume, tags, visibilite, ia_assiste: iaAssiste ? 1 : 0, commentaire };
      let slug;
      if (nouvelle) {
        corps.statut = publier ? "publie" : "brouillon";
        const r = await api.creer(corps);
        slug = r.slug;
      } else {
        corps.versionBase = page.version;
        const r = await api.modifier(page.id, corps);
        slug = r.slug;
        if (publier && page.statut !== "publie") await api.statut(page.id, "publie");
      }
      wikiEffacerBrouillon(page);
      onEnregistre(slug);
    } catch (e) { setErreur(e.message); }
    setEnCours("");
  };

  const lancerIA = async (quoi) => {
    setEnCours(quoi); setErreur("");
    try {
      if (quoi === "redaction") {
        const r = await api.ia("redaction", { titre, notes, categorie });
        setContenu(r.contenu); setIaAssiste(true); setAssistant(false);
      } else if (quoi === "resume") {
        const r = await api.ia("resume", { contenu });
        setResume(r.resume);
      } else if (quoi === "relecture") {
        const r = await api.ia("relecture", { contenu });
        setRelecture(r.propositions.map((p, i) => ({ ...p, _id: i, _etat: "attente" })));
      }
    } catch (e) { setErreur(e.message); }
    setEnCours("");
  };

  const accepter = (p) => {
    if (!contenu.includes(p.original)) {
      setRelecture((r) => r.map((x) => (x._id === p._id ? { ...x, _etat: "introuvable" } : x)));
      return;
    }
    setContenu((c) => c.replace(p.original, p.propose));
    setRelecture((r) => r.map((x) => (x._id === p._id ? { ...x, _etat: "accepte" } : x)));
  };

  const televerser = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setEnCours("upload");
    try {
      const r = await api.televerser(page ? page.id : 0, f);
      inserer(`\n${r.markdown}\n`);
    } catch (err) { setErreur(err.message); }
    setEnCours("");
    if (fichierRef.current) fichierRef.current.value = "";
  };

  const outils = [
    ["B", () => entourer("**", "**"), { fontWeight: 800 }],
    ["I", () => entourer("*", "*"), { fontStyle: "italic" }],
    ["H2", () => inserer("\n## "), {}],
    ["• Liste", () => inserer("\n- "), {}],
    ["1. Liste", () => inserer("\n1. "), {}],
    ["⌗ Tableau", () => inserer("\n| Colonne | Colonne |\n|---|---|\n| … | … |\n"), {}],
    ["</> Code", () => inserer("\n```\n\n```\n"), {}],
    ["❝ Citation", () => inserer("\n> "), {}],
    ["🔗 Lien", () => entourer("[", "](https://)"), {}],
    ["[[ page ]]", () => inserer("[[slug-de-la-page]]"), {}],
    ["🎫 #ticket", () => inserer("#ticket:"), {}],
    ["👤 @joueur", () => inserer("@joueur:"), {}],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={onAnnuler} style={wGhostSm}>← Annuler</button>
        <h2 style={{ color: THEME.text, fontSize: 17, fontWeight: 800, margin: 0 }}>
          {nouvelle ? "✏️ Nouvelle page" : `✏️ ${page.titre}`}
        </h2>
        <div style={{ flex: 1 }} />
        <button onClick={() => enregistrer(false)} disabled={enCours === "save"} style={wGhostSm}>
          {enCours === "save" ? "…" : "Enregistrer le brouillon"}
        </button>
        {droits.redacteur && (
          <button onClick={() => enregistrer(true)} disabled={enCours === "save"} style={wBtnSm(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>
            ✓ {nouvelle ? "Publier" : `Publier la version ${page.version + 1}`}
          </button>
        )}
      </div>

      {erreur && (
        <div style={{ background: THEME.redDim, border: `1px solid ${THEME.red}`, color: THEME.red, borderRadius: 8, padding: "10px 14px", fontSize: 12.5 }}>{erreur}</div>
      )}

      {brouillonLocal && (
        <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: THEME.amber, fontSize: 12.5, flex: 1, minWidth: 200 }}>
            💾 Un brouillon non enregistré traîne dans ce navigateur ({wikiIlYA(brouillonLocal.horodatage)}) — tu veux le reprendre ?
          </span>
          <button onClick={() => { wikiEffacerBrouillon(page); setBrouillonLocal(null); }} style={wGhostSm}>Ignorer</button>
          <button onClick={reprendreBrouillon} style={wBtnSm(THEME.amberDim, THEME.amber, THEME.amber)}>Reprendre</button>
        </div>
      )}

      <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre de la page" style={{ ...wInp, fontSize: 15, fontWeight: 700 }} />

      {/* Assistant */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, borderRadius: 10, padding: "10px 12px" }}>
        <span style={{ color: THEME.purple, fontWeight: 700, fontSize: 12, alignSelf: "center" }}>🤖 ASSISTANT</span>
        <button onClick={() => setAssistant(!assistant)} style={wBtnSm("transparent", THEME.purple, THEME.purple)}>✍️ Rédiger un brouillon</button>
        <button onClick={() => lancerIA("resume")} disabled={!!enCours} style={wBtnSm("transparent", THEME.purple, THEME.purple)}>
          {enCours === "resume" ? "⏳ …" : "📝 Résumer"}
        </button>
        <button onClick={() => lancerIA("relecture")} disabled={!!enCours} style={wBtnSm("transparent", THEME.purple, THEME.purple)}>
          {enCours === "relecture" ? "⏳ …" : "🪶 Relire"}
        </button>
        <span style={{ color: THEME.textDim, fontSize: 11, alignSelf: "center" }}>l'assistant propose, tu valides</span>
      </div>

      {assistant && (
        <div style={{ ...wPanel, borderColor: THEME.purple }}>
          <div style={{ ...wLbl, marginBottom: 8 }}>Tes notes en vrac — l'assistant en fera une page structurée</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
            placeholder="ex : rembourser seulement si c'est la faute du serveur, admin valide jusqu'à 5000 orions, au-delà c'est un fondateur, toujours passer par un ticket"
            style={{ ...wInp, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button onClick={() => setAssistant(false)} style={wGhostSm}>Fermer</button>
            <button onClick={() => lancerIA("redaction")} disabled={!titre.trim() || enCours === "redaction"} style={wBtnSm(THEME.purpleDim, THEME.purple, THEME.purple)}>
              {enCours === "redaction" ? "⏳ Rédaction…" : "✍️ Rédiger"}
            </button>
          </div>
          <div style={{ color: THEME.textDim, fontSize: 11, marginTop: 8 }}>
            Le texte produit remplacera le contenu actuel et la page sera marquée « assisté par IA » jusqu'à ta relecture.
          </div>
        </div>
      )}

      {relecture && (
        <div style={{ ...wPanel, borderColor: THEME.purple }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <div style={{ color: THEME.purple, fontSize: 12, fontWeight: 700 }}>
              🪶 {relecture.length} proposition{relecture.length > 1 ? "s" : ""} de relecture
            </div>
            <button onClick={() => setRelecture(null)} style={{ ...wGhostSm, marginLeft: "auto" }}>Fermer</button>
          </div>
          {relecture.length === 0 && <div style={{ color: THEME.emerald, fontSize: 13 }}>✓ Rien à corriger, le texte est propre.</div>}
          {relecture.map((p) => (
            <div key={p._id} style={{ background: THEME.surface, borderRadius: 8, padding: 12, marginBottom: 8, opacity: p._etat === "attente" ? 1 : 0.55 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ ...wLbl, marginBottom: 4 }}>Original</div>
                  <div style={{ fontSize: 12.5, color: THEME.textMuted, lineHeight: 1.5 }}>{p.original}</div>
                </div>
                <div>
                  <div style={{ ...wLbl, marginBottom: 4, color: THEME.purple }}>Proposition {p.raison ? `— ${p.raison}` : ""}</div>
                  <div style={{ fontSize: 12.5, color: THEME.text, lineHeight: 1.5 }}>{p.propose}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8, alignItems: "center" }}>
                {p._etat === "accepte" && <span style={{ color: THEME.emerald, fontSize: 12 }}>✓ appliqué</span>}
                {p._etat === "refuse" && <span style={{ color: THEME.textDim, fontSize: 12 }}>refusé</span>}
                {p._etat === "introuvable" && <span style={{ color: THEME.amber, fontSize: 12 }}>⚠️ le passage a changé depuis</span>}
                {p._etat === "attente" && (
                  <>
                    <button onClick={() => setRelecture((r) => r.map((x) => (x._id === p._id ? { ...x, _etat: "refuse" } : x)))} style={wGhostSm}>Refuser</button>
                    <button onClick={() => accepter(p)} style={wBtnSm(THEME.emeraldDim, THEME.emerald, THEME.emerald)}>✓ Accepter</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barre d'outils */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {outils.map(([lbl, fn, extra]) => (
          <button key={lbl} onClick={fn} style={{ ...wGhostSm, ...extra }}>{lbl}</button>
        ))}
        <input ref={fichierRef} type="file" onChange={televerser} style={{ display: "none" }} />
        <button onClick={() => fichierRef.current && fichierRef.current.click()} disabled={enCours === "upload"} style={wGhostSm}>
          {enCours === "upload" ? "⏳ envoi…" : "🖼️ Image / fichier"}
        </button>
      </div>

      {isMobile && (
        <div style={{ display: "flex", gap: 6 }}>
          {[["ecrire", "Écrire"], ["apercu", "Aperçu"]].map(([id, lbl]) => (
            <button key={id} onClick={() => setOnglet(id)}
              style={wBtnSm(onglet === id ? THEME.accentDim : "transparent", onglet === id ? THEME.accent : THEME.border, onglet === id ? THEME.accent : THEME.textMuted)}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
        {(!isMobile || onglet === "ecrire") && (
          <textarea ref={zone} value={contenu} onChange={(e) => setContenu(e.target.value)}
            placeholder={"## Une section\n\nÉcris ici, en Markdown.\n\n- une liste\n- un autre point\n\nRéfère un ticket avec #ticket:42, un joueur avec @joueur:Pseudo,\nune autre page avec [[slug-de-la-page]]."}
            style={{ ...wInp, background: "#0D1117", minHeight: 440, fontFamily: "'Courier New', monospace", fontSize: 12.5, lineHeight: 1.6, resize: "vertical" }} />
        )}
        {(!isMobile || onglet === "apercu") && (
          <div style={{ ...wPanel, minHeight: 440, overflowX: "auto" }}>
            <div style={{ ...wLbl, marginBottom: 10 }}>Aperçu</div>
            <WikiContenu html={apercu} onChip={() => {}} />
          </div>
        )}
      </div>

      <div style={wPanel}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
          <div>
            <div style={{ ...wLbl, marginBottom: 5 }}>Catégorie</div>
            <select value={categorie} onChange={(e) => setCategorie(e.target.value)} style={wInp}>
              {Object.entries(WIKI_CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ ...wLbl, marginBottom: 5 }}>Visibilité</div>
            <select value={visibilite} onChange={(e) => setVisibilite(e.target.value)} disabled={!droits.redacteur} style={{ ...wInp, opacity: droits.redacteur ? 1 : 0.5 }}>
              <option value="staff">Tout le staff</option>
              <option value="admin">Admins et fondateurs</option>
              {droits.fondateur && <option value="fondateur">Fondateurs seuls</option>}
            </select>
          </div>
          <div>
            <div style={{ ...wLbl, marginBottom: 5 }}>Tags (séparés par des virgules)</div>
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="remboursement, erp" style={wInp} />
          </div>
          <div style={{ gridColumn: isMobile ? "auto" : "1 / 3" }}>
            <div style={{ ...wLbl, marginBottom: 5 }}>Résumé (affiché dans les listes)</div>
            <input value={resume} onChange={(e) => setResume(e.target.value)} placeholder="Une à deux phrases — ou clique sur « 📝 Résumer »" style={wInp} />
          </div>
          <div>
            <div style={{ ...wLbl, marginBottom: 5 }}>Commentaire de version</div>
            <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="ex : plafond porté à 5 000 Orions" style={wInp} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          {!droits.redacteur && (
            <span style={{ color: THEME.textDim, fontSize: 11 }}>
              Ton rôle te permet de préparer des brouillons — un admin les relira avant publication.
            </span>
          )}
          <span style={{ color: THEME.textDim, fontSize: 11, marginLeft: "auto" }}>
            {sauvegardeLe ? `💾 brouillon gardé dans ce navigateur (${wikiIlYA(sauvegardeLe)})` : "💾 sauvegarde automatique dans ce navigateur"}
          </span>
        </div>
      </div>
    </div>
  );
}
